import { Link, NavLink, useNavigate } from "react-router-dom";
import { FlaskConical, LogOut, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSlip } from "@/context/SlipContext";
import { TierBadge } from "@/components/TierBadge";
import { NotificationBell } from "@/components/NotificationBell";
import { StreakChip } from "@/components/StreakChip";

const LINKS = [
  { to: "/dashboard", label: "Daily Picks", id: "nav-daily-picks" },
  { to: "/favorites", label: "Lab Favorites", id: "nav-favorites" },
  { to: "/props", label: "Props", id: "nav-props" },
  { to: "/players", label: "Players", id: "nav-players" },
  { to: "/standings", label: "Standings", id: "nav-standings" },
  { to: "/builder", label: "Builder", id: "nav-parlay-builder" },
  { to: "/parlays", label: "My Parlays", id: "nav-my-parlays" },
  { to: "/performance", label: "Performance", id: "nav-performance" },
  { to: "/leaderboard", label: "Leaderboard", id: "nav-leaderboard" },
  { to: "/contests", label: "Contests", id: "nav-contests" },
  { to: "/pricing", label: "Pricing", id: "nav-pricing" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { legs } = useSlip();
  const navigate = useNavigate();
  const links = user ? LINKS : LINKS.filter((l) => ["/leaderboard", "/pricing"].includes(l.to));

  return (
    <header className="sticky top-0 z-40 overflow-x-clip border-b border-[#26334D]/70 bg-[#0B0E14]/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-logo" className="flex shrink-0 items-center gap-2.5">
          <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
            <FlaskConical size={18} />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />
          </span>
          <span className="font-display whitespace-nowrap text-xl font-black uppercase tracking-tight text-white sm:text-2xl">
            The Parlay <span className="text-emerald-400">Lab</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-1 xl:flex" data-testid="nav-links">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.id}
              className={({ isActive }) => `relative whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${isActive ? "text-emerald-400 bg-emerald-500/10" : "text-slate-400 hover:text-slate-100 hover:bg-[#182030]"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {user && (
            <button data-testid="nav-slip-btn" onClick={() => navigate("/builder")} className="btn-ghost !px-3.5 !py-2 relative hidden xl:inline-flex">
              <Ticket size={16} /> <span className="hidden sm:inline">Slip</span>
              {legs.length > 0 && <span data-testid="nav-slip-count" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-500 px-1 font-mono-num text-[11px] font-bold text-[#0B0E14]">{legs.length}</span>}
            </button>
          )}
          {user ? (
            <div className="flex items-center gap-1.5 sm:gap-3" data-testid="nav-user-profile">
              <StreakChip />
              <NotificationBell />
              <Link to="/billing" data-testid="nav-billing-link" title="Billing & plan"><TierBadge tier={user.tier} /></Link>
              <span className="hidden whitespace-nowrap text-sm text-slate-300 2xl:inline" data-testid="nav-user-name">{user.name}</span>
              <button data-testid="nav-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="hidden rounded-full p-2 text-slate-400 transition-colors hover:bg-[#182030] hover:text-white sm:inline-flex" title="Log out">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <>
              <Link to="/track-record" data-testid="nav-track-record-link" className="hidden text-sm font-medium text-slate-400 hover:text-white sm:inline">Track record</Link>
              <Link to="/login" data-testid="nav-auth-login-btn" className="btn-ghost !py-2">Log in</Link>
              <Link to="/register" data-testid="nav-auth-signup-btn" className="btn-primary !py-2">Get Started</Link>
            </>
          )}
        </div>
      </div>
      {user && (
        <nav className="flex gap-1 overflow-x-auto px-4 pb-2 xl:hidden">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${isActive ? "bg-emerald-500/10 text-emerald-400" : "text-slate-400"}`}>{l.label}</NavLink>
          ))}
          <button data-testid="nav-mobile-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium text-slate-500 sm:hidden">Log out</button>
        </nav>
      )}
    </header>
  );
}
